import React, { useState } from 'react';
import { Clock, CheckCircle, XCircle, Star, Users, Zap } from 'lucide-react';
import { useVendorPerformance } from '../../services/vendorAnalyticsService';

const VendorPerformance = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useVendorPerformance(period);

  const performance = data?.data || {};

  // Helper function to format numeric values safely
  const formatValue = (value, formatter) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return formatter(value);
  };

  const performanceMetrics = [
    {
      label: 'Response Time',
      value: formatValue(performance.response_time, (val) => `${Number(val).toFixed(1)} hrs`),
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      label: 'Completion Rate',
      value: formatValue(performance.completion_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      label: 'Cancellation Rate',
      value: formatValue(performance.cancellation_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
    {
      label: 'Client Satisfaction',
      value: formatValue(performance.client_satisfaction, (val) => `${Number(val).toFixed(2)}/5`),
      icon: Star,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50'
    },
    {
      label: 'Repeat Client Rate',
      value: formatValue(performance.repeat_client_rate, (val) => `${Number(val).toFixed(1)}%`),
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      label: 'Service Efficiency',
      value: formatValue(performance.service_efficiency, (val) => `${Number(val).toFixed(1)}%`),
      icon: Zap,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50'
    }
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Performance</h1>
          <p className="text-sm text-gray-600">Track your business performance metrics</p>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="flex space-x-4 border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { value: 'week', label: 'This Week' },
          { value: 'month', label: 'This Month' },
          { value: 'quarter', label: 'This Quarter' },
          { value: 'year', label: 'This Year' }
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setPeriod(tab.value)}
            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors duration-200 ${
              period === tab.value
                ? 'border-black text-black'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-32"></div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          Failed to load performance data. Please try again.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {performanceMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                  <div className={`w-12 h-12 ${metric.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${metric.color}`} />
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{metric.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                </div>
              );
            })}
          </div>

          {performance.peak_hours && performance.peak_hours.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Peak Hours</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {performance.peak_hours.map((hour, index) => (
                  <div key={index} className="text-center">
                    <p className="text-sm text-gray-600">{hour.hour}</p>
                    <p className="text-lg font-semibold text-gray-900">{hour.bookings || 0}</p>
                    <p className="text-xs text-gray-500">bookings</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default VendorPerformance;

